import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { applicationsService, Application, MyApplication, ApplicationsListParams, ApplicationsListResponse, MyApplicationsListResponse } from '@/services/applications';

interface ApplicationsState {
  applications: Application[];
  myApplications: MyApplication[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  } | null;
  myApplicationsPagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  } | null;
  isLoading: boolean;
  myApplicationsLoading: boolean;
  error: string | null;
  myApplicationsError: string | null;
  currentFeedId: string | null;
  feedDetails: {
    title?: string;
    type?: string;
    applicationCount?: number;
  } | null;
}

const initialState: ApplicationsState = {
  applications: [],
  myApplications: [],
  pagination: null,
  myApplicationsPagination: null,
  isLoading: false,
  myApplicationsLoading: false,
  error: null,
  myApplicationsError: null,
  currentFeedId: null,
  feedDetails: null,
};

export const fetchApplicationsByFeedId = createAsyncThunk(
  'applications/fetchByFeedId',
  async ({ feedId, params }: { feedId: string; params?: ApplicationsListParams }, { rejectWithValue }) => {
    try {
      const response = await applicationsService.getApplicationsByFeedId(feedId, params);
      return { feedId, ...response };
    } catch (error: unknown) {
      console.error('Error fetching applications:', error);
      let errorMessage = 'Failed to fetch applications';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        errorMessage = apiError.response?.data?.message || errorMessage;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchMyApplications = createAsyncThunk(
  'applications/fetchMyApplications',
  async ({ params }: { params?: ApplicationsListParams }, { rejectWithValue }) => {
    try {
      const response = await applicationsService.getMyApplications(params);
      return response;
    } catch (error: unknown) {
      console.error('Error fetching my applications:', error);
      let errorMessage = 'Failed to fetch my applications';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        errorMessage = apiError.response?.data?.message || errorMessage;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMyApplicationsError: (state) => {
      state.myApplicationsError = null;
    },
    resetApplicationsState: () => initialState,
    setFeedDetails: (state, action: PayloadAction<{ title?: string; type?: string; applicationCount?: number }>) => {
      state.feedDetails = action.payload;
    },
    clearApplications: (state) => {
      state.applications = [];
      state.pagination = null;
      state.currentFeedId = null;
      state.feedDetails = null;
    },
    clearMyApplications: (state) => {
      state.myApplications = [];
      state.myApplicationsPagination = null;
    },
    updateApplicationStatus: (state, action: PayloadAction<{ applicationId: string; status: string }>) => {
      const { applicationId, status } = action.payload;
      const applicationIndex = state.applications.findIndex(app => app._id === applicationId);
      if (applicationIndex !== -1) {
        state.applications[applicationIndex].status = status as any;
      }

      const myApplicationIndex = state.myApplications.findIndex(app => app._id === applicationId);
      if (myApplicationIndex !== -1) {
        state.myApplications[myApplicationIndex].status = status as any;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Applications by Feed ID
      .addCase(fetchApplicationsByFeedId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApplicationsByFeedId.fulfilled, (state, action: PayloadAction<ApplicationsListResponse & { feedId: string }>) => {
        console.log(action.payload)

        state.isLoading = false;
        state.applications = action.payload.data.docs;
        state.pagination = {
          current: action.payload.data.page,
          pages: action.payload.data.totalPages,
          total: action.payload.data.totalDocs,
          limit: action.payload.data.limit,
        };
        state.currentFeedId = action.payload.feedId;
        state.error = null;
      })
      .addCase(fetchApplicationsByFeedId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.applications = [];
        state.pagination = null;
      })
      // Fetch My Applications
      .addCase(fetchMyApplications.pending, (state) => {
        state.myApplicationsLoading = true;
        state.myApplicationsError = null;
      })
      .addCase(fetchMyApplications.fulfilled, (state, action: PayloadAction<MyApplicationsListResponse>) => {
        state.myApplicationsLoading = false;
        state.myApplications = action.payload.data.docs;
        state.myApplicationsPagination = {
          current: action.payload.data.page,
          pages: action.payload.data.totalPages,
          total: action.payload.data.totalDocs,
          limit: action.payload.data.limit,
        };
        state.myApplicationsError = null;
      })
      .addCase(fetchMyApplications.rejected, (state, action) => {
        state.myApplicationsLoading = false;
        state.myApplicationsError = action.payload as string;
        state.myApplications = [];
        state.myApplicationsPagination = null;
      });
  },
});

export const { clearError, clearMyApplicationsError, resetApplicationsState, setFeedDetails, clearApplications, clearMyApplications, updateApplicationStatus } = applicationsSlice.actions;
export default applicationsSlice.reducer;